import { useTheme } from "next-themes";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from "recharts";
import { CHART_STYLES } from "../../constants";
import { CustomLegend } from "./custom-legend";
import { CustomTooltip } from "./custom-tooltip";
import type { FC } from "react";

interface ResponsiblePersonMetric {
  readonly name: string;
  readonly count: number;
  readonly percentage: number;
}

interface ResponsiblePersonsChartProps {
  readonly data: readonly ResponsiblePersonMetric[];
}

/**
 * Displays the count of organizations per responsible person in a bar chart.
 */
export const ResponsiblePersonsChart: FC<ResponsiblePersonsChartProps> = ({
  data,
}) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const commonTooltipProps = {
    contentStyle: {
      backgroundColor: isDark ? "#1f2937" : "#ffffff",
      border: `1px solid ${isDark ? "#374151" : "#e5e7eb"}`,
      borderRadius: "8px",
      fontSize: "12px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    },
    itemStyle: { color: isDark ? "#f9fafb" : "#1f2937" },
    cursor: false as const,
    formatter: (value: number) => [`${value} orgs`, "Count"],
    labelFormatter: (label: string) => `Responsible Person: ${label}`,
  };

  const commonLegendProps = {
    wrapperStyle: { fontSize: CHART_STYLES.fontSize },
    iconSize: CHART_STYLES.legend.iconSize,
  };

  return (
    <div className="h-[400px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={[...data]} layout="vertical" margin={{ left: 100 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          <YAxis type="category" dataKey="name" width={100} />
          <Tooltip content={<CustomTooltip />} {...commonTooltipProps} />
          <Legend content={<CustomLegend />} {...commonLegendProps} />
          <Bar dataKey="count" fill="#3B82F6" radius={[0, 4, 4, 0]}>
            {[...data].map((_, index) => (
              <Cell key={`cell-${index}`} fill="#3B82F6" />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
